import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Group,
  Loader,
  Modal,
  NumberInput,
  PageHeader,
  Select,
  Stack,
  TabView,
  Text,
  TextInput,
  resolveApiOrigin,
} from '@vipso/shared';
import {
  useCancelBillingSubscriptionMutation,
  useCreateBillingSubscriptionMutation,
  useDeleteBillingOverrideMutation,
  useGetBillingBundlesQuery,
  useGetBillingInvoicesQuery,
  useGetBillingModulesQuery,
  useGetBillingOverridesQuery,
  useGetBillingSubscriptionsQuery,
  useGetOrgsQuery,
  useGetTenantsQuery,
  useMarkBillingInvoicePaidMutation,
  useSeedBillingDefaultsMutation,
  useUpsertBillingBundleMutation,
  useUpsertBillingModuleMutation,
  useUpsertBillingOverrideMutation,
  useVoidBillingInvoiceMutation,
  type BillingModule,
} from '../api/adminApi';
import { ModalFooter } from '../components/ModalFooter';
import { ModuleCheckboxList } from '../components/ModuleCheckboxList';

function yearlyPreview(monthly: number, discountPct: number) {
  const m = Number(monthly) || 0;
  const d = Math.min(100, Math.max(0, Number(discountPct) || 0));
  return Math.round(m * 12 * (1 - d / 100) * 100) / 100;
}

function ModulesTab() {
  const { data, isLoading, isError } = useGetBillingModulesQuery();
  const [upsert, upsertState] = useUpsertBillingModuleMutation();
  const [seedDefaults, seedState] = useSeedBillingDefaultsMutation();
  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BillingModule | null>(null);
  const [form, setForm] = useState({
    key: '',
    name: '',
    meter: 'flat',
    monthlyPrice: 200,
    yearlyDiscountPct: 10,
    enabledModuleTag: '',
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);

  const modules = data?.billingModules ?? [];

  function openCreate() {
    setEditing(null);
    setForm({
      key: '',
      name: '',
      meter: 'flat',
      monthlyPrice: 200,
      yearlyDiscountPct: 10,
      enabledModuleTag: '',
      isActive: true,
    });
    setError(null);
    setOpen(true);
  }

  function openEdit(m: BillingModule) {
    setEditing(m);
    setForm({
      key: m.key,
      name: m.name,
      meter: m.meter,
      monthlyPrice: Number(m.monthlyPrice),
      yearlyDiscountPct: Number(m.yearlyDiscountPct),
      enabledModuleTag: m.enabledModuleTag ?? '',
      isActive: m.isActive,
    });
    setError(null);
    setOpen(true);
  }

  async function save() {
    setError(null);
    try {
      await upsert({
        id: editing?.id,
        key: form.key,
        name: form.name,
        meter: form.meter,
        monthlyPrice: form.monthlyPrice,
        yearlyDiscountPct: form.yearlyDiscountPct,
        enabledModuleTag: form.enabledModuleTag || null,
        isActive: form.isActive,
        featureKeys: editing?.featureKeys ?? [],
        currency: 'INR',
      }).unwrap();
      setOpen(false);
    } catch {
      setError('Failed to save module');
    }
  }

  if (isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {isError ? <Alert color="red" title="Failed to load modules" /> : null}
      <Group justify="space-between">
        <Text c="dimmed" size="sm">
          Global catalog. Yearly = monthly × 12 × (1 − discount%). Edit prices anytime.
        </Text>
        <Group gap="sm">
          <Button
            variant="default"
            loading={seedState.isLoading}
            onClick={() => setSeedConfirmOpen(true)}
          >
            Seed / refresh defaults
          </Button>
          <Button onClick={openCreate}>Add module</Button>
        </Group>
      </Group>
      {seedState.isSuccess ? (
        <Alert color="green" title="Defaults seeded">
          VIPSo Annual catalog refreshed and org licenses backfilled.
        </Alert>
      ) : null}
      {seedState.isError ? <Alert color="red" title="Seed failed" /> : null}
      <ConfirmDialog
        opened={seedConfirmOpen}
        onClose={() => setSeedConfirmOpen(false)}
        onConfirm={() => {
          setSeedConfirmOpen(false);
          void seedDefaults();
        }}
        title="Seed billing defaults"
        message="This upserts the global VIPSo Annual catalog modules and bundle. In production it requires ALLOW_BILLING_SEED=true. Continue?"
        confirmLabel="Seed defaults"
      />
      <DataTable
        columns={[
          { key: 'id', header: 'ID', render: (r: BillingModule) => r.id },
          { key: 'name', header: 'Name', render: (r: BillingModule) => r.name },
          { key: 'key', header: 'Key', render: (r: BillingModule) => r.key },
          { key: 'meter', header: 'Meter', render: (r: BillingModule) => r.meter },
          {
            key: 'monthly',
            header: 'Monthly (INR)',
            render: (r: BillingModule) => Number(r.monthlyPrice).toFixed(0),
          },
          {
            key: 'discount',
            header: 'Yearly discount',
            render: (r: BillingModule) => `${Number(r.yearlyDiscountPct)}%`,
          },
          {
            key: 'yearly',
            header: 'Yearly (INR)',
            render: (r: BillingModule) =>
              yearlyPreview(Number(r.monthlyPrice), Number(r.yearlyDiscountPct)).toFixed(0),
          },
          {
            key: 'active',
            header: 'Active',
            render: (r: BillingModule) => (
              <Badge color={r.isActive ? 'green' : 'gray'}>{r.isActive ? 'yes' : 'no'}</Badge>
            ),
          },
          {
            key: 'actions',
            header: '',
            render: (r: BillingModule) => (
              <Button size="xs" variant="light" onClick={() => openEdit(r)}>
                Edit
              </Button>
            ),
          },
        ]}
        rows={modules}
        getRowId={(r) => r.id}
        testId="billing-modules-table"
      />

      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title={editing ? 'Edit module' : 'Add module'}
      >
        <Stack gap="md">
          {error ? <Alert color="red" title={error} /> : null}
          <TextInput
            label="Key"
            value={form.key}
            onChange={(e) => setForm((f) => ({ ...f, key: e.currentTarget.value }))}
            required
            disabled={Boolean(editing)}
          />
          <TextInput
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))}
            required
          />
          <Select
            label="Meter"
            value={form.meter}
            onChange={(v) => setForm((f) => ({ ...f, meter: v || 'flat' }))}
            data={[
              { value: 'flat', label: 'Flat (per org)' },
              { value: 'per_user', label: 'Per user' },
            ]}
          />
          <NumberInput
            label="Monthly price (INR)"
            value={form.monthlyPrice}
            onChange={(v) =>
              setForm((f) => ({ ...f, monthlyPrice: typeof v === 'number' ? v : 0 }))
            }
            min={0}
          />
          <NumberInput
            label="Yearly discount %"
            value={form.yearlyDiscountPct}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                yearlyDiscountPct: typeof v === 'number' ? v : 0,
              }))
            }
            min={0}
            max={100}
          />
          <Text size="sm" c="dimmed">
            Yearly preview: ₹{yearlyPreview(form.monthlyPrice, form.yearlyDiscountPct)}
          </Text>
          <TextInput
            label="Enabled module tag"
            value={form.enabledModuleTag}
            onChange={(e) =>
              setForm((f) => ({ ...f, enabledModuleTag: e.currentTarget.value }))
            }
          />
          <Checkbox
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.currentTarget.checked }))}
          />
          <ModalFooter
            onCancel={() => setOpen(false)}
            onSubmit={() => void save()}
            loading={upsertState.isLoading}
            disabled={!form.key || !form.name}
          />
        </Stack>
      </Modal>
    </Stack>
  );
}

function OverridesTab() {
  const { data: tenantsData } = useGetTenantsQuery({ page: 1, pageLimit: 100 });
  const [tenantId, setTenantId] = useState('');
  const { data: modulesData } = useGetBillingModulesQuery();
  const { data, isLoading } = useGetBillingOverridesQuery(
    { tenantId },
    { skip: !tenantId },
  );
  const [upsert] = useUpsertBillingOverrideMutation();
  const [remove] = useDeleteBillingOverrideMutation();
  const [moduleId, setModuleId] = useState('');
  const [monthlyPrice, setMonthlyPrice] = useState<number | ''>('');
  const [yearlyDiscountPct, setYearlyDiscountPct] = useState<number | ''>('');

  const tenantOptions = useMemo(
    () =>
      (tenantsData?.tenants.rows ?? []).map((t) => ({
        value: t.id,
        label: `${t.name} (${t.slug})`,
      })),
    [tenantsData],
  );
  const moduleOptions = useMemo(
    () =>
      (modulesData?.billingModules ?? []).map((m) => ({
        value: m.id,
        label: m.name,
      })),
    [modulesData],
  );
  const overrides = data?.billingModulePriceOverrides ?? [];

  async function saveOverride() {
    if (!tenantId || !moduleId) return;
    await upsert({
      tenantId,
      moduleId,
      monthlyPrice: monthlyPrice === '' ? null : monthlyPrice,
      yearlyDiscountPct: yearlyDiscountPct === '' ? null : yearlyDiscountPct,
    }).unwrap();
    setModuleId('');
    setMonthlyPrice('');
    setYearlyDiscountPct('');
  }

  return (
    <Stack gap="md">
      <Select
        label="Tenant"
        placeholder="Select tenant"
        data={tenantOptions}
        value={tenantId}
        onChange={(v) => setTenantId(v || '')}
      />
      {!tenantId ? (
        <Text c="dimmed" size="sm">
          Select a tenant to view / edit price overrides.
        </Text>
      ) : isLoading ? (
        <Loader />
      ) : (
        <>
          <DataTable
            columns={[
              { key: 'id', header: 'ID', render: (r: (typeof overrides)[0]) => r.id },
              {
                key: 'module',
                header: 'Module',
                render: (r: (typeof overrides)[0]) => r.module?.name ?? r.moduleId,
              },
              {
                key: 'moduleId',
                header: 'Module ID',
                render: (r: (typeof overrides)[0]) => r.moduleId,
              },
              {
                key: 'monthly',
                header: 'Monthly override',
                render: (r: (typeof overrides)[0]) =>
                  r.monthlyPrice == null ? 'inherit' : Number(r.monthlyPrice),
              },
              {
                key: 'discount',
                header: 'Yearly discount override',
                render: (r: (typeof overrides)[0]) =>
                  r.yearlyDiscountPct == null
                    ? 'inherit'
                    : `${Number(r.yearlyDiscountPct)}%`,
              },
              {
                key: 'actions',
                header: '',
                render: (r: (typeof overrides)[0]) => (
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    onClick={() => void remove({ id: r.id })}
                  >
                    Remove
                  </Button>
                ),
              },
            ]}
            rows={overrides}
            getRowId={(r) => r.id}
            testId="billing-overrides-table"
          />
          <Group align="flex-end" grow>
            <Select
              label="Module"
              data={moduleOptions}
              value={moduleId}
              onChange={(v) => setModuleId(v || '')}
            />
            <NumberInput
              label="Monthly (blank = inherit)"
              value={monthlyPrice}
              onChange={(v) => setMonthlyPrice(typeof v === 'number' ? v : '')}
            />
            <NumberInput
              label="Yearly % (blank = inherit)"
              value={yearlyDiscountPct}
              onChange={(v) => setYearlyDiscountPct(typeof v === 'number' ? v : '')}
            />
            <Button disabled={!moduleId} onClick={() => void saveOverride()}>
              Save override
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}

function BundlesTab() {
  const { data, isLoading } = useGetBillingBundlesQuery();
  const { data: modulesData } = useGetBillingModulesQuery();
  const [upsert, upsertState] = useUpsertBillingBundleMutation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [discount, setDiscount] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);

  const bundles = data?.billingPlanBundles ?? [];
  const modules = modulesData?.billingModules ?? [];

  async function save() {
    await upsert({
      name,
      slug,
      bundleDiscountPct: discount,
      moduleIds: selected,
      isActive: true,
    }).unwrap();
    setOpen(false);
    setName('');
    setSlug('');
    setDiscount(0);
    setSelected([]);
  }

  if (isLoading) return <Loader />;

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button onClick={() => setOpen(true)}>Create bundle</Button>
      </Group>
      <DataTable
        columns={[
          { key: 'id', header: 'ID', render: (r: (typeof bundles)[0]) => r.id },
          { key: 'name', header: 'Name', render: (r: (typeof bundles)[0]) => r.name },
          { key: 'slug', header: 'Slug', render: (r: (typeof bundles)[0]) => r.slug },
          {
            key: 'discount',
            header: 'Bundle discount',
            render: (r: (typeof bundles)[0]) => `${Number(r.bundleDiscountPct)}%`,
          },
          {
            key: 'modules',
            header: 'Modules',
            render: (r: (typeof bundles)[0]) =>
              (r.modules ?? []).map((m) => m.name).join(', ') || '—',
          },
        ]}
        rows={bundles}
        getRowId={(r) => r.id}
        testId="billing-bundles-table"
      />
      <Modal opened={open} onClose={() => setOpen(false)} title="Create plan bundle">
        <Stack gap="md">
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <TextInput label="Slug" value={slug} onChange={(e) => setSlug(e.currentTarget.value)} />
          <NumberInput
            label="Bundle discount %"
            value={discount}
            onChange={(v) => setDiscount(typeof v === 'number' ? v : 0)}
            min={0}
            max={100}
          />
          <Text fw={500} size="sm">
            Modules
          </Text>
          <ModuleCheckboxList
            modules={modules}
            selected={selected}
            onChange={setSelected}
            withPrice
          />
          <ModalFooter
            onCancel={() => setOpen(false)}
            onSubmit={() => void save()}
            loading={upsertState.isLoading}
            disabled={!name || !slug || !selected.length}
          />
        </Stack>
      </Modal>
    </Stack>
  );
}

function SubscriptionsTab() {
  const { data, isLoading } = useGetBillingSubscriptionsQuery();
  const { data: orgsData } = useGetOrgsQuery({ status: 'Active', page: 1, pageLimit: 100 });
  const { data: bundlesData } = useGetBillingBundlesQuery();
  const { data: modulesData } = useGetBillingModulesQuery();
  const [createSub, createState] = useCreateBillingSubscriptionMutation();
  const [cancelSub] = useCancelBillingSubscriptionMutation();
  const [open, setOpen] = useState(false);
  const [orgId, setOrgId] = useState('');
  const [bundleId, setBundleId] = useState('');
  const [period, setPeriod] = useState('monthly');
  const [moduleIds, setModuleIds] = useState<string[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  const rows = data?.billingSubscriptions ?? [];

  async function create() {
    setMsg(null);
    const result = await createSub({
      orgId,
      billingPeriod: period,
      bundleId: bundleId || undefined,
      moduleIds: bundleId ? undefined : moduleIds,
    }).unwrap();
    // Domain patches the payment banner via org-data-changed (no force-refresh flicker).
    setMsg(
      `Created subscription + invoice ${result.createBillingSubscription.invoice.invoiceNumber} (₹${result.createBillingSubscription.invoice.total})`,
    );
    setOpen(false);
  }

  if (isLoading) return <Loader />;

  return (
    <Stack gap="md">
      {msg ? <Alert color="green" title={msg} /> : null}
      <Group justify="flex-end">
        <Button onClick={() => setOpen(true)}>Create subscription</Button>
      </Group>
      <DataTable
        columns={[
          { key: 'id', header: 'ID', render: (r: (typeof rows)[0]) => r.id },
          {
            key: 'org',
            header: 'Org',
            render: (r: (typeof rows)[0]) => r.org?.name ?? r.orgId,
          },
          {
            key: 'orgId',
            header: 'Org ID',
            render: (r: (typeof rows)[0]) => r.orgId,
          },
          { key: 'period', header: 'Period', render: (r: (typeof rows)[0]) => r.billingPeriod },
          {
            key: 'status',
            header: 'Status',
            render: (r: (typeof rows)[0]) => <Badge>{r.status}</Badge>,
          },
          {
            key: 'items',
            header: 'Items',
            render: (r: (typeof rows)[0]) =>
              (r.items ?? []).map((i) => i.module?.name ?? i.moduleId).join(', '),
          },
          {
            key: 'actions',
            header: '',
            render: (r: (typeof rows)[0]) =>
              r.status !== 'cancelled' ? (
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  onClick={() => void cancelSub({ id: r.id })}
                >
                  Cancel
                </Button>
              ) : null,
          },
        ]}
        rows={rows}
        getRowId={(r) => r.id}
        testId="billing-subs-table"
      />
      <Modal opened={open} onClose={() => setOpen(false)} title="Create subscription">
        <Stack gap="md">
          <Select
            label="Organization"
            data={(orgsData?.orgs.rows ?? []).map((o) => ({
              value: o.id,
              label: o.name,
            }))}
            value={orgId}
            onChange={(v) => setOrgId(v || '')}
          />
          <Select
            label="Billing period"
            data={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'yearly', label: 'Yearly (discount applied)' },
            ]}
            value={period}
            onChange={(v) => setPeriod(v || 'monthly')}
          />
          <Select
            label="Bundle (optional)"
            clearable
            data={(bundlesData?.billingPlanBundles ?? []).map((b) => ({
              value: b.id,
              label: b.name,
            }))}
            value={bundleId}
            onChange={(v) => setBundleId(v || '')}
          />
          {!bundleId ? (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Or pick modules
              </Text>
              <ModuleCheckboxList
                modules={modulesData?.billingModules ?? []}
                selected={moduleIds}
                onChange={setModuleIds}
              />
            </Stack>
          ) : null}
          <ModalFooter
            onCancel={() => setOpen(false)}
            onSubmit={() => void create()}
            submitLabel="Create (pending invoice)"
            loading={createState.isLoading}
            disabled={!orgId || (!bundleId && !moduleIds.length)}
          />
        </Stack>
      </Modal>
    </Stack>
  );
}

function InvoicesTab() {
  const { data, isLoading } = useGetBillingInvoicesQuery();
  const [markPaid, markState] = useMarkBillingInvoicePaidMutation();
  const [voidInv] = useVoidBillingInvoiceMutation();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const rows = data?.billingInvoices ?? [];

  async function confirmPaid() {
    if (!confirmId) return;
    await markPaid({ invoiceId: confirmId }).unwrap();
    // Domain clearOrgLockAfterPayment emits one quiet force-refresh.
    setMsg('Invoice marked paid — subscription activated');
    setConfirmId(null);
  }

  if (isLoading) return <Loader />;

  return (
    <Stack gap="md">
      {msg ? <Alert color="green" title={msg} /> : null}
      <DataTable
        columns={[
          { key: 'id', header: 'ID', render: (r: (typeof rows)[0]) => r.id },
          {
            key: 'number',
            header: 'Invoice',
            render: (r: (typeof rows)[0]) => r.invoiceNumber,
          },
          {
            key: 'org',
            header: 'Org',
            render: (r: (typeof rows)[0]) => r.org?.name ?? r.orgId,
          },
          {
            key: 'orgId',
            header: 'Org ID',
            render: (r: (typeof rows)[0]) => r.orgId,
          },
          {
            key: 'total',
            header: 'Total',
            render: (r: (typeof rows)[0]) =>
              `₹${Number(r.total).toFixed(0)} ${r.currency}`,
          },
          {
            key: 'status',
            header: 'Status',
            render: (r: (typeof rows)[0]) => (
              <Badge
                color={
                  r.status === 'paid' ? 'green' : r.status === 'void' ? 'gray' : 'yellow'
                }
              >
                {r.status}
              </Badge>
            ),
          },
          {
            key: 'actions',
            header: '',
            render: (r: (typeof rows)[0]) => (
              <Group gap="xs">
                {r.pdfFileId ? (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => {
                      const origin = resolveApiOrigin();
                      if (!origin) return;
                      window.open(
                        `${origin}/api/files/${r.pdfFileId}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }}
                  >
                    Download invoice
                  </Button>
                ) : null}
                {r.status === 'pending' ? (
                  <>
                    <Button size="xs" onClick={() => setConfirmId(r.id)}>
                      Mark paid
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => void voidInv({ invoiceId: r.id })}
                    >
                      Void
                    </Button>
                  </>
                ) : null}
              </Group>
            ),
          },
        ]}
        rows={rows}
        getRowId={(r) => r.id}
        testId="billing-invoices-table"
      />
      <ConfirmDialog
        opened={Boolean(confirmId)}
        onClose={() => setConfirmId(null)}
        title="Mark invoice as paid?"
        message="This activates the organization subscription and entitlements."
        confirmLabel="Mark paid"
        onConfirm={() => void confirmPaid()}
        loading={markState.isLoading}
      />
    </Stack>
  );
}

export function BillingPage() {
  return (
    <Stack gap="lg">
      <PageHeader
        title="Billing & plans"
        description="Module catalog, tenant price overrides, bundles, subscriptions, and invoices. Mark invoices paid to activate plans."
      />
      <TabView
        tabs={[
          { value: 'modules', title: 'Modules', content: <ModulesTab /> },
          { value: 'overrides', title: 'Tenant overrides', content: <OverridesTab /> },
          { value: 'bundles', title: 'Bundles', content: <BundlesTab /> },
          { value: 'subscriptions', title: 'Subscriptions', content: <SubscriptionsTab /> },
          { value: 'invoices', title: 'Invoices', content: <InvoicesTab /> },
        ]}
      />
    </Stack>
  );
}
